import React, { useState, useMemo, useRef, useEffect } from "react";
import "./settings.css"; // keeps your global settings page look tokens
import AccountChart from "./Accounting/account_chart";
import TotalJournals from "./Accounting/TotalJournals";
import BankTransactions from "./Accounting/BankTransactions";
import "./transactions.css";
import {
  Wallet2,
  BookOpenCheck,
  Banknote,
  ChevronRight,
  Circle,
} from "lucide-react";

const Settings = () => {
  const [activeTab, setActiveTab] = useState("AccountChart");
  const tabsRef = useRef(null);

  const tabs = useMemo(
    () => [
      {
        key: "AccountChart",
        label: "Chart of Accounts",
        icon: <Wallet2 size={16} />,
      },
      {
        key: "TotalJournals",
        label: "Journals",
        icon: <BookOpenCheck size={16} />,
      },
      {
        key: "BankTransactions",
        label: "Bank Transactions",
        icon: <Banknote size={16} />,
      },
    ],
    []
  );

  // keyboard arrow navigation across tabs
  useEffect(() => {
    const el = tabsRef.current;
    if (!el) return;
    const handler = (e) => {
      if (!["ArrowLeft", "ArrowRight"].includes(e.key)) return;
      const idx = tabs.findIndex((t) => t.key === activeTab);
      const nextIdx =
        e.key === "ArrowRight"
          ? (idx + 1) % tabs.length
          : (idx - 1 + tabs.length) % tabs.length;
      setActiveTab(tabs[nextIdx].key);
    };
    el.addEventListener("keydown", handler);
    return () => el.removeEventListener("keydown", handler);
  }, [activeTab, tabs]);

  const renderTabContent = () => {
    switch (activeTab) {
      case "AccountChart":
        return <AccountChart />;
      case "TotalJournals":
        return <TotalJournals />;
      case "BankTransactions":
        return <BankTransactions />;
      default:
        return <BankTransactions />;
    }
  };

  return (
    <div className="tx-page">
      {/* Sticky header */}
      <header className="tx-header">
        <div className="tx-title">
          <h1>Transactions</h1>
          <p className="tx-subtitle">
            Manage your chart of accounts, record journals, and reconcile bank
            activity.
          </p>
        </div>
        <div className="tx-breadcrumb">
          <Circle size={8} /> Finance <ChevronRight size={14} /> Transactions
        </div>
      </header>

      {/* Modern tabs */}
      <div
        className="tx-tabs"
        role="tablist"
        aria-label="Transactions Sections"
        ref={tabsRef}
        tabIndex={0}
      >
        {tabs.map((t) => {
          const isActive = activeTab === t.key;
          return (
            <button
              key={t.key}
              role="tab"
              aria-selected={isActive}
              aria-controls={`panel-${t.key}`}
              className={`tx-tab ${isActive ? "active" : ""}`}
              onClick={() => setActiveTab(t.key)}
            >
              <span className="tx-tab-icon">{t.icon}</span>
              <span className="tx-tab-label">{t.label}</span>
            </button>
          );
        })}
      </div>

      {/* Content card */}
      <section
        id={`panel-${activeTab}`}
        role="tabpanel"
        aria-labelledby={activeTab}
        className="tx-panel"
      >
        {renderTabContent()}
      </section>
    </div>
  );
};

export default Settings;
